import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getThreatDetailApi } from '../../queries/threats';
import {
  fetchGSBLiveData,
  getNetworkDataByType,
  getHistoryStatuesApi,
} from '../../services/singleThreatApi';
import {
  fetchTakedownStatuses,
  getTakedownDetailApi,
  takedownStatusesQueryKey,
} from '../../queries/takedowns';
import {
  SingleThreatDetailsType,
  DNSDataResponseType,
  GSBLiveInfoType,
} from '../../context/SingleThreat/SingleThreatType';
import { ThreatTypes, HistoryStatues } from '../../helpers/types';

interface CachedData {
  threatDetail: SingleThreatDetailsType | null;
  gsbLiveInfo: GSBLiveInfoType | undefined;
  dnsRecords: DNSDataResponseType | undefined;
  statusHistory: HistoryStatues[] | undefined;
  takedownItems: any[] | undefined;
  takedownStatuses: any[] | undefined;
}

interface UseThreatExpandDataReturn {
  threatDetail: SingleThreatDetailsType | null;
  gsbLiveInfo: GSBLiveInfoType | undefined;
  dnsRecords: DNSDataResponseType | undefined;
  statusHistory: HistoryStatues[] | undefined;
  takedownItems: any[] | undefined;
  takedownStatuses: any[] | undefined;
  isLoading: boolean;
  gsbLoading: boolean;
  dnsLoading: boolean;
  statusHistoryLoading: boolean;
  takedownLoading: boolean;
}

const useThreatExpandData = (
  threat: ThreatTypes,
  isExpanded: boolean,
): UseThreatExpandDataReturn => {
  const [threatDetail, setThreatDetail] =
    useState<SingleThreatDetailsType | null>(null);
  const [gsbLiveInfo, setGsbLiveInfo] = useState<GSBLiveInfoType | undefined>();
  const [dnsRecords, setDnsRecords] = useState<
    DNSDataResponseType | undefined
  >();
  const [statusHistory, setStatusHistory] = useState<
    HistoryStatues[] | undefined
  >();
  const [takedownItems, setTakedownItems] = useState<any[] | undefined>();
  const [takedownStatuses, setTakedownStatuses] = useState<any[] | undefined>();

  const [isLoading, setIsLoading] = useState(false);
  const [gsbLoading, setGsbLoading] = useState(false);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [takedownLoading, setTakedownLoading] = useState(false);

  const cacheRef = useRef<Record<number, CachedData>>({});
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isExpanded) return;

    const threatId = threat.threat_id;

    if (cacheRef.current[threatId]) {
      const cached = cacheRef.current[threatId];
      setThreatDetail(cached.threatDetail);
      setGsbLiveInfo(cached.gsbLiveInfo);
      setDnsRecords(cached.dnsRecords);
      setStatusHistory(cached.statusHistory);
      setTakedownItems(cached.takedownItems);
      setTakedownStatuses(cached.takedownStatuses);
      return;
    }

    let cancelled = false;

    // Fire each API call independently so data renders as it arrives
    setIsLoading(true);
    setGsbLoading(true);
    setDnsLoading(true);
    setStatusHistoryLoading(true);

    getThreatDetailApi({
      protected_id: String(threat.protected_asset_id),
      threat_id: String(threat.threat_id),
    })
      .then(res => {
        if (cancelled) return;
        const detail = res?.data ?? null;
        setThreatDetail(detail);
        setIsLoading(false);
        cacheRef.current[threatId] = {
          ...cacheRef.current[threatId],
          threatDetail: detail,
        };
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });

    fetchGSBLiveData(threat.value?.value || threat.threat_name)
      .then(res => {
        if (cancelled) return;
        const gsb = res?.data;
        setGsbLiveInfo(gsb);
        setGsbLoading(false);
        cacheRef.current[threatId] = {
          ...cacheRef.current[threatId],
          gsbLiveInfo: gsb,
        };
      })
      .catch(() => {
        if (!cancelled) setGsbLoading(false);
      });

    getNetworkDataByType(threat.threat_id, 'records')
      .then(res => {
        if (cancelled) return;
        const dns = res?.data;
        setDnsRecords(dns);
        setDnsLoading(false);
        cacheRef.current[threatId] = {
          ...cacheRef.current[threatId],
          dnsRecords: dns,
        };
      })
      .catch(() => {
        if (!cancelled) setDnsLoading(false);
      });

    getHistoryStatuesApi({
      d: threat.protected_asset_id,
      t: threat.threat_id,
    })
      .then(res => {
        if (cancelled) return;
        const history = res?.data;
        setStatusHistory(history);
        setStatusHistoryLoading(false);
        cacheRef.current[threatId] = {
          ...cacheRef.current[threatId],
          statusHistory: history,
        };
      })
      .catch(() => {
        if (!cancelled) setStatusHistoryLoading(false);
      });

    // Takedown data — only for confirmed threats
    if (threat.identification_status?.value === 'confirmed') {
      setTakedownLoading(true);

      Promise.all([
        getTakedownDetailApi({
          asset_id: threat.protected_asset_id,
          threat_id: threat.threat_id,
        }),
        // Shared query cache (WHEEL-339): expanding many rows fetches the
        // status catalogue once instead of once per row
        queryClient.fetchQuery({
          queryKey: takedownStatusesQueryKey,
          queryFn: fetchTakedownStatuses,
          staleTime: 5 * 60_000,
        }),
      ])
        .then(([detailRes, statuses]) => {
          if (cancelled) return;
          const items = detailRes?.data?.items || [];
          setTakedownItems(items);
          setTakedownStatuses(statuses);
          setTakedownLoading(false);
          cacheRef.current[threatId] = {
            ...cacheRef.current[threatId],
            takedownItems: items,
            takedownStatuses: statuses,
          };
        })
        .catch(() => {
          if (!cancelled) setTakedownLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isExpanded, threat.threat_id]);

  return {
    threatDetail,
    gsbLiveInfo,
    dnsRecords,
    statusHistory,
    takedownItems,
    takedownStatuses,
    isLoading,
    gsbLoading,
    dnsLoading,
    statusHistoryLoading,
    takedownLoading,
  };
};

export default useThreatExpandData;
